import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { OrderDto, WaybillDto, OrderStatus, OrderGoodsItemDto, ChangeOrderStatusDto } from '../../api/ClientApi';
import { WarehouseClient, dispatchOrder } from '../../api/Extensions';
import { myAssert } from '../../common/console';
import { RootState } from '../reducers';
import {
    addOrUpdateOrder,
    addOrderGoodsItemToOrder,
    removeOrderFromRedux,
    removeOrderGoodsItemFromOrder,
} from './orderActions';
import { addOrUpdateMultipleReduxGoods } from './goodsActions';
import { filterUniqueBareGoodsByArticle } from './helper';
import { addOrUpdateDemandOrder } from './demandOrderActions';
import { addOrUpdateReturnOrder } from './returnOrderActions';
import { updateReduxSite } from './siteActions';
import { importApprovedOrderGoodsItems } from './orderGoodsItemsActions';

export const createNewEmptyWaybill = createAction('waybill/createNewEmptyWaybill');

export const createNewWaybill = createAction<WaybillDto>('waybill/createNewWaybill');

export const clearWaybill = createAction('waybill/clearWaybill');

export const importWaybill = createAsyncThunk<WaybillDto, number>(
    'waybill/importWaybill',
    async (payload, thunkAPI) => {
        return WarehouseClient.getWaybill(payload, true);
    },
);

export const updateWaybillPickupDate = createAction<Date | undefined>('waybill/updateWaybillPickupDate');
export const updateWaybillDeliveryDate = createAction<Date | undefined>('waybill/updateWaybillDeliveryDate');

export const updateWaybillData = createAction<string>('waybill/updateWaybillData');

export const updateWaybillDescription = createAction<string>('waybill/updateWaybillDescription');

export const updateWaybillDriverName = createAction<string>('waybill/updateWaybillDriverName');

export const updateWaybillDriverUniqueNumber = createAction<string>('waybill/updateWaybillDriverUniqueNumber');

export const updateWaybillLicensePlateNum = createAction<string>('waybill/updateWaybillLicensePlateNum');

export const saveWaybill = createAsyncThunk<WaybillDto, number>(
    'waybill/saveWaybill',
    async (payload: number, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const waybillState = rootState.waybillForm;

        const order = rootState.orderForm.order ? new OrderDto(rootState.orderForm.order) : null;
        const bareGoods = rootState.goodsToOrderForm.bareGoodsEntities;

        const waybill = waybillState.waybill;

        myAssert(() => waybill !== null);

        if (!order) {
            throw new Error();
        }

        const orderId: number | undefined = order.id;

        const newOrder = new OrderDto(order);

        const filteredBareGoods = filterUniqueBareGoodsByArticle(bareGoods ?? []);

        if (waybill === null || !orderId || orderId <= 0) {
            throw new Error();
        }

        if (!order) {
            throw new Error();
        }

        const newWaybill = new WaybillDto(waybill);
        newWaybill.orderId = orderId;
        newWaybill.order = order;

        const filteredOrderGoodsItems = newOrder.orderGoodsItems;

        let savedOrderGoodsItems: OrderGoodsItemDto[] = [];
        let removedGoods: number[] = [];

        const savedOrderGoodsItemsPromises: Promise<OrderGoodsItemDto>[] = [];
        const removedOrderGoodsItemsIdsPromises: Promise<number>[] = [];

        // go through oldGoods;
        filteredOrderGoodsItems?.map((fg) => {
            // check if this goods exists in new order
            const orderedGoods = filteredBareGoods?.find((bg) => bg.article?.id === fg.article?.id);

            if (!!orderedGoods) {
                // if quantity is different, update existing goods quantity
                if (orderedGoods.orderedQuantity !== fg.quantity) {
                    fg.quantity = orderedGoods.orderedQuantity;

                    const addOrderGoodsItemPromise = WarehouseClient.addNewOrderGoodsItemForOrder(orderId, fg).then(
                        (res) => {
                            thunkAPI.dispatch(addOrderGoodsItemToOrder({ orderId, orderGoodsItem: res }));
                            return res;
                        },
                    );

                    savedOrderGoodsItemsPromises.push(addOrderGoodsItemPromise);
                }
            } else {
                // this goods does not exist in new order, so we have to delete it and disconnect it from the order
                if (newOrder.orderStatus === OrderStatus.Approved) {
                    const removedOrderGoodsItemsIdsPromise = WarehouseClient.deleteOrderGoodsItem(fg.id ?? 0).then(
                        () => {
                            thunkAPI.dispatch(
                                removeOrderGoodsItemFromOrder({
                                    orderId: newOrder.id ?? 0,
                                    orderGoodsItemId: fg.id,
                                }),
                            );
                            return fg.id ?? 0;
                        },
                    );
                    removedOrderGoodsItemsIdsPromises.push(removedOrderGoodsItemsIdsPromise);
                }
            }
        });

        filteredBareGoods
            .filter((fbg) => !filteredOrderGoodsItems?.find((fg) => fg.article?.id === fbg.article?.id))
            .map((fbg) => {
                // in this case, goods does not exist in old order, so we have to add id and connect it to the order
                const newOrderGood = new OrderGoodsItemDto();
                newOrderGood.articleId = fbg.article?.id;
                newOrderGood.article = fbg.article;
                newOrderGood.order = undefined;
                newOrderGood.orderId = undefined;
                newOrderGood.quantity = fbg.orderedQuantity;

                const addOrderGoodsItemPromise = WarehouseClient.addNewOrderGoodsItemForOrder(
                    orderId,
                    newOrderGood,
                ).then((res) => {
                    thunkAPI.dispatch(addOrderGoodsItemToOrder({ orderId, orderGoodsItem: res }));
                    return res;
                });

                savedOrderGoodsItemsPromises.push(addOrderGoodsItemPromise);
            });

        savedOrderGoodsItems = await Promise.all(savedOrderGoodsItemsPromises);

        removedGoods = await Promise.all(removedOrderGoodsItemsIdsPromises);

        newOrder.orderGoodsItems = newOrder.orderGoodsItems?.filter(
            (ogi) => !savedOrderGoodsItems.find((sogi) => sogi.id === ogi.id),
        );
        newOrder.orderGoodsItems?.push(...savedOrderGoodsItems);
        newOrder.orderGoodsItems = newOrder.orderGoodsItems?.filter((ogi) => !removedGoods.includes(ogi.id ?? 0));

        if (!!newOrder.demandOrderId) {
            WarehouseClient.getDemandOrder(newOrder.demandOrderId).then((res) =>
                thunkAPI.dispatch(addOrUpdateDemandOrder(res)),
            );
        }

        if (!!newOrder.returnOrderId) {
            WarehouseClient.getReturnOrder(newOrder.returnOrderId).then((res) =>
                thunkAPI.dispatch(addOrUpdateReturnOrder(res)),
            );
        }

        // One backend transaction: waybill + reservation of every ordered article + status Transport.
        newWaybill.order = undefined;

        const step = await dispatchOrder(orderId, newWaybill);

        thunkAPI.dispatch(addOrUpdateMultipleReduxGoods(step.goods));
        thunkAPI.dispatch(addOrUpdateOrder(step.order));
        thunkAPI.dispatch(importApprovedOrderGoodsItems());
        thunkAPI.dispatch(updateReduxSite(order.originSiteId ?? 0));

        return step.order.waybill ?? newWaybill;
    },
    {
        condition: (payload: number, thunkAPI: { getState: any }) => {
            const waybillState = (thunkAPI.getState() as RootState).waybillForm;

            return waybillState.waybill !== null;
        },
    },
);
